import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Image,
  Film,
  FileText,
  Globe,
  Rocket,
  AudioLines,
  FileQuestion,
  Loader2,
  AlertCircle,
  Ban,
  Clock,
  Play,
  PlaySquare,
} from 'lucide-react';
import { cn } from '@scio3/ui';
import type { AssetLike } from '@scio3/shared-types';
import { FILE_TYPES, ASSET_STATUS, ASSET_TYPES, MEDIA_TYPES } from '@scio3/shared-types';
import { useSignedUrl } from '@/lib/file-cdn';
import { getAssetDef, getAppMetaImageUrl } from '../utils/app-meta';
import { PLAYLIST_THUMBNAIL_URL } from '@/features/playlists/utils/playlist-constants';

interface AssetThumbnailProps {
  asset: AssetLike;
  className?: string;
  /** Fixed-size variant. Ignored when `fill` is true. */
  size?: 'sm' | 'md' | 'lg';
  /** When true, thumbnail expands to fill its parent container (h-full w-full). */
  fill?: boolean;
  /** When true, hides the app icon overlay on the thumbnail (e.g., when the icon is shown in a separate type column). */
  hideAppIconOverlay?: boolean;
}

function getFallbackIcon(asset: AssetLike) {
  // Handle nested playlists (type === 'playlist')
  // PlaylistAsset has type?: string, so 'playlist' is valid when cast to Asset
  if ((asset.type as string) === MEDIA_TYPES.PLAYLIST) return PlaySquare;
  if (asset.type === ASSET_TYPES.APP || asset.type === ASSET_TYPES.WEB) return Rocket;
  switch (asset.fileType) {
    case FILE_TYPES.IMAGE:
      return Image;
    case FILE_TYPES.VIDEO:
      return Film;
    case FILE_TYPES.AUDIO:
      return AudioLines;
    case FILE_TYPES.DOCUMENT:
      return FileText;
    case FILE_TYPES.WEB:
      return Globe;
    default:
      return FileQuestion;
  }
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-16 w-16',
};

const iconSizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

const overlayIconClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

const playIconClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-6 w-6',
};

/**
 * Returns true if the asset has a "full-takeover" status (CONVERTING/FAILED)
 * where the thumbnail should show only the status icon — no fallback content underneath.
 */
function isFullStatusTakeover(asset: AssetLike): boolean {
  return asset.status === ASSET_STATUS.CONVERTING || asset.status === ASSET_STATUS.FAILED;
}

/**
 * Returns an overlay for statuses that layer on top of valid content
 * (expired, scheduled). CONVERTING/FAILED are handled separately as full takeovers.
 */
function getStatusOverlay(asset: AssetLike, size: 'sm' | 'md' | 'lg', fill: boolean) {
  const iconClass = fill ? 'h-6 w-6' : overlayIconClasses[size];

  const expired = asset.assetExpired;
  if (!expired) return null;

  // Convert "display-time-as-UTC" dates back to local time (see AssetStatusBadge)
  const fromDisplayUtc = (iso: string) => {
    const d = new Date(iso);
    return new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  };
  const now = new Date();
  const endDate = expired.endDate ? fromDisplayUtc(expired.endDate) : null;
  const startDate = expired.startDate ? fromDisplayUtc(expired.startDate) : null;

  // Expired: endDate in the past
  if (endDate && endDate < now) {
    return (
      <div className="absolute inset-0 flex items-center justify-center rounded bg-background/60">
        <Ban className={cn(iconClass, 'text-muted-foreground')} />
      </div>
    );
  }

  // Scheduled: startDate in the future (not yet live)
  if (startDate && startDate > now) {
    return (
      <div className="absolute inset-0 flex items-center justify-center rounded bg-background/60">
        <Clock className={cn(iconClass, 'text-info')} />
      </div>
    );
  }

  // Expiring: endDate in the future (asset is live but will expire)
  if (endDate && endDate > now) {
    return (
      <div className="absolute inset-0 flex items-center justify-center rounded bg-background/60">
        <Clock className={cn(iconClass, 'text-destructive')} />
      </div>
    );
  }

  return null;
}

export function AssetThumbnail({
  asset,
  className,
  size = 'md',
  fill = false,
  hideAppIconOverlay = false,
}: AssetThumbnailProps) {
  const { t } = useTranslation();
  // PlaylistAsset has type?: string, so 'playlist' is valid when cast to Asset
  const isPlaylist = asset.type === MEDIA_TYPES.PLAYLIST;
  // For nested playlists without a thumbnail, use the CDN playlist image
  const rawThumbnailUrl = asset.thumbnail || (isPlaylist ? PLAYLIST_THUMBNAIL_URL : undefined);
  const thumbnailUrl = useSignedUrl(rawThumbnailUrl, asset.bucket);
  const FallbackIcon = getFallbackIcon(asset);
  const overlay = getStatusOverlay(asset, size, fill);
  const assetDef = getAssetDef(asset);
  const bgColor = assetDef.thumbnailBackgroundPrimaryColor;

  const appMetaImageUrl = getAppMetaImageUrl(assetDef);

  const [imgError, setImgError] = useState(false);
  const [appMetaImgError, setAppMetaImgError] = useState(false);

  // Reset error when URL changes so a newly-signed URL gets a chance to load.
  // Without this, a 403 from the S3 fallback (auth not yet loaded) sets imgError=true
  // permanently — even after useSignedUrl recomputes with valid CDN credentials.
  useEffect(() => {
    setImgError(false);
  }, [thumbnailUrl]);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const animatedVideoRef = useRef<HTMLVideoElement>(null);

  const isVideo = asset.fileType === FILE_TYPES.VIDEO;
  const videoUrl = useSignedUrl(asset.AWSS3ID, asset.bucket);
  const isAnimatedThumbnail = rawThumbnailUrl?.endsWith('.mp4') ?? false;

  const handleMouseEnter = useCallback(() => {
    if (isAnimatedThumbnail) {
      animatedVideoRef.current?.play().catch(() => {});
    } else if (isVideo && videoUrl) {
      setIsHovering(true);
      videoRef.current?.play().catch(() => {});
    }
  }, [isAnimatedThumbnail, isVideo, videoUrl]);

  const handleMouseLeave = useCallback(() => {
    if (isAnimatedThumbnail) {
      if (animatedVideoRef.current) {
        animatedVideoRef.current.pause();
        animatedVideoRef.current.currentTime = 0;
      }
    } else if (isVideo) {
      setIsHovering(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isAnimatedThumbnail, isVideo]);

  const containerClass = fill
    ? 'relative flex h-full w-full items-center justify-center overflow-hidden bg-muted'
    : cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded bg-muted',
        sizeClasses[size],
      );

  const iconClass = fill ? 'h-10 w-10' : iconSizeClasses[size];
  const playClass = fill ? 'h-8 w-8' : playIconClasses[size];

  const bgStyle = bgColor ? { backgroundColor: bgColor } : undefined;

  // App icon overlay — shown in top-left corner when asset has both a thumbnail and an app meta icon
  // App icon overlay — hide on sm (too small), scale for md/lg/fill
  const showAppIconOverlay =
    !hideAppIconOverlay &&
    thumbnailUrl &&
    !imgError &&
    appMetaImageUrl &&
    !appMetaImgError &&
    !!asset.appType &&
    (fill || size !== 'sm');
  const appIconOverlay = showAppIconOverlay ? (
    <div
      className={cn(
        'absolute z-10 flex items-center justify-center rounded-md bg-white/90 shadow-sm',
        fill
          ? 'bottom-1.5 left-1.5 h-7 w-7'
          : size === 'lg'
            ? 'bottom-1 left-1 h-6 w-6'
            : 'bottom-0.5 left-0.5 h-5 w-5',
      )}
    >
      <img
        src={appMetaImageUrl}
        alt=""
        className={cn('object-contain', fill ? 'h-5 w-5' : size === 'lg' ? 'h-4 w-4' : 'h-3 w-3')}
        loading="lazy"
        onError={() => setAppMetaImgError(true)}
      />
    </div>
  ) : null;

  // CONVERTING / FAILED — show a clean dedicated status, no content underneath
  if (isFullStatusTakeover(asset)) {
    const isConverting = asset.status === ASSET_STATUS.CONVERTING;
    return (
      <div className={cn(containerClass, className)} style={bgStyle}>
        {isConverting ? (
          <Loader2 className={cn('animate-spin text-warning', fill ? 'h-8 w-8' : iconClass)} />
        ) : (
          <AlertCircle className={cn('text-destructive', fill ? 'h-8 w-8' : iconClass)} />
        )}
      </div>
    );
  }

  // Tier 1 failed (no S3 thumbnail or it errored) → try other sources
  if (!thumbnailUrl || imgError) {
    // Tier 2: Video first frame — use the video file itself with preload="metadata"
    if (isVideo && videoUrl) {
      return (
        <div className={cn(containerClass, className)} style={bgStyle}>
          <video
            src={videoUrl}
            className="h-full w-full object-contain"
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Play className={cn('fill-white text-white', playClass)} />
          </div>
          {overlay}
        </div>
      );
    }

    // Tier 3: AppMeta image (CDN URL like https://cdn.optisigns.com/scio/images/feeds.png)
    // Rendered smaller and centered (app logos shouldn't fill the entire card)
    if (appMetaImageUrl && !appMetaImgError) {
      const appMetaImgClass = fill ? 'h-16 w-16' : iconSizeClasses[size];
      return (
        <div className={cn(containerClass, className)} style={bgStyle}>
          <img
            src={appMetaImageUrl}
            alt={asset.originalFileName || t('Asset thumbnail')}
            className={cn('object-contain', appMetaImgClass)}
            loading="lazy"
            onError={() => setAppMetaImgError(true)}
          />
          {overlay}
        </div>
      );
    }

    // Tier 4: Generic file-type icon
    return (
      <div className={cn(containerClass, className)} style={bgStyle}>
        <FallbackIcon className={cn('text-muted-foreground', iconClass)} />
        {overlay}
      </div>
    );
  }

  // Animated .mp4 thumbnail → render <video>
  if (isAnimatedThumbnail) {
    return (
      <div
        className={cn(containerClass, className)}
        style={bgStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {appIconOverlay}
        <video
          ref={animatedVideoRef}
          src={thumbnailUrl}
          className={cn('h-full w-full', 'object-contain')}
          muted
          loop
          playsInline
          preload="metadata"
        />
        {overlay}
      </div>
    );
  }

  // Standard image thumbnail
  return (
    <div
      className={cn(containerClass, className)}
      style={bgStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Blurred background fill for object-contain gaps — skip for playlist icons (they have transparency) */}
      {!isPlaylist && (
        <img
          src={thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
          aria-hidden="true"
          loading="lazy"
        />
      )}
      {appIconOverlay}
      <img
        src={thumbnailUrl}
        alt={asset.originalFileName || t('Asset thumbnail')}
        className={cn(
          'relative h-full w-full',
          'object-contain',
          isHovering && isVideo && 'hidden',
        )}
        loading="lazy"
        onError={() => setImgError(true)}
      />

      {/* Video preview on hover (plays actual video from AWSS3ID) */}
      {isVideo && videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className={cn(
            'absolute inset-0 h-full w-full',
            'object-contain',
            isHovering ? 'block' : 'hidden',
          )}
          muted
          loop
          playsInline
          preload="metadata"
        />
      )}

      {/* Play icon overlay for videos (shown when not hovering) */}
      {isVideo && !isHovering && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play className={cn('fill-white text-white', playClass)} />
        </div>
      )}

      {overlay}
    </div>
  );
}
